import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Typography,
  Button,
  FormLabel,
  TextField,
  Switch,
} from '@mui/material';
import { useFormik } from 'formik';
import { isEmpty } from 'lodash';
import { useNotify, useRefresh, useUpdate } from 'react-admin';
import { DataProviderResource } from 'src/constants';
import { AccountApplicationCategory } from 'src/models';
import * as yup from 'yup';

interface EditApplicationCategoryFormValues {
  note?: string;
  isResolved?: boolean;
  totalRequestedDocuments?: number;
}

const validationSchema = yup.object().shape({
  note: yup.string().max(255, 'Note must be at most 255 characters long'),
  isResolved: yup.boolean(),
  totalRequestedDocuments: yup
    .number()
    .positive('Total Requested Documents must be a positive number')
    .integer('Total Requested Documents must be an integer'),
});

interface EditApplicationCategoryModalProps {
  applicationId: string;
  accountId: string;
  isOpen: boolean;
  onClose: () => void;
  category: AccountApplicationCategory;
}

export const EditApplicationCategoryModal = ({
  applicationId,
  accountId,
  isOpen,
  onClose,
  category,
}: EditApplicationCategoryModalProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading }] = useUpdate();

  const isMultiDocumentCategory = () => category.requestedDocuments.length > 1;

  const formik = useFormik<EditApplicationCategoryFormValues>({
    initialValues: {
      isResolved: category.isResolved,
      note: category.adminNote,
      totalRequestedDocuments: category.totalRequestedDocuments,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values: EditApplicationCategoryFormValues) => {
      update(
        DataProviderResource.AccountApplication,
        {
          id: applicationId,
          meta: { accountId },
          data: {
            categories: [
              {
                name: category.name.value,
                isResolved: values.isResolved,
                adminNote: isEmpty(values.note) ? null : values.note,
                totalRequestedDocuments: values.totalRequestedDocuments,
              },
            ],
          },
        },
        {
          onError: (error: any) => {
            notify(error.message, { type: 'error' });
          },
          onSuccess: () => {
            notify('Application category updated successfully', { type: 'success' });
            refresh();
            onClose();
          },
        },
      );
    },
  });

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth>
      <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
        <DialogTitle>Edit Application Category</DialogTitle>
        <DialogContent>
          <Grid container direction='column' spacing={2}>
            <Grid item xs={6} md={6}>
              <FormControl margin='dense' variant='standard' fullWidth sx={{ width: '100%' }}>
                <FormLabel id='type'>Category Name</FormLabel>
                <Typography variant='body1'>{category.name.label}</Typography>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={6}>
              <FormControl margin='dense' variant='standard' fullWidth sx={{ width: '100%' }}>
                <FormLabel id='file'>Note</FormLabel>
                <TextField
                  defaultValue={category.adminNote}
                  value={formik.values.note}
                  onChange={event => {
                    formik.setFieldValue('note', event.target.value);
                  }}
                />
                {formik.errors.note && (
                  <Typography variant='caption' color='error'>
                    {formik.errors.note}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            {isMultiDocumentCategory() ? (
              <Grid item xs={6} md={6}>
                <FormControl margin='dense' variant='standard' fullWidth sx={{ width: '100%' }}>
                  <FormLabel id='type'>Total Requested Documents</FormLabel>
                  <TextField
                    defaultValue={category.totalRequestedDocuments}
                    value={formik.values.totalRequestedDocuments}
                    onChange={event => {
                      formik.setFieldValue('totalRequestedDocuments', event.target.value);
                    }}
                  />
                  {formik.errors.totalRequestedDocuments && (
                    <Typography variant='caption' color='error'>
                      {formik.errors.totalRequestedDocuments}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            ) : null}
            {isMultiDocumentCategory() ? (
              <Grid item xs={6} md={6}>
                <FormControl margin='dense' variant='standard' fullWidth sx={{ width: '100%' }}>
                  <FormLabel id='type'>Is Resolved</FormLabel>
                  <Switch
                    defaultChecked={category.isResolved}
                    checked={formik.values.isResolved}
                    onChange={event => {
                      formik.setFieldValue('isResolved', event.target.checked);
                    }}
                  />
                  {formik.errors.isResolved && (
                    <Typography variant='caption' color='error'>
                      {formik.errors.isResolved}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid item xs={12} container direction='row' justifyContent='flex-end' spacing={2}>
            <Grid item>
              <Button variant='outlined' color='inherit' size='medium' onClick={onClose}>
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button variant='contained' color='primary' size='medium' disabled={isLoading} type='submit'>
                Edit
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </form>
    </Dialog>
  );
};
